import { useMemo } from 'react';
import type { ChannelWithId } from '../lib/data';
import { isEmptyProjectSet } from '../lib/status';
import { StatusCard } from '../components/StatusCard';

/**
 * Per-product status board. Renders an explicit warning when no channels are
 * configured — an empty project must never read as all-green (R12).
 */
export function StatusBoard({
  channels,
  nowMs,
  onOpen,
  loading = false,
}: {
  channels: ChannelWithId[];
  nowMs: number;
  onOpen?: (channel: ChannelWithId) => void;
  loading?: boolean;
}) {
  const byProduct = useMemo(() => {
    const map = new Map<string, ChannelWithId[]>();
    for (const c of channels) {
      const list = map.get(c.productId) ?? [];
      list.push(c);
      map.set(c.productId, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [channels]);

  // While the channel subscription is still resolving, defer the empty-state
  // warning so a cold load never flashes "no channels configured" (R3, R4).
  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  if (isEmptyProjectSet(channels.length)) {
    return (
      <div className="warning-panel" role="status">
        No channels are configured yet. Run the seed script to create the four launch channels.
      </div>
    );
  }

  return (
    <div className="board">
      {byProduct.map(([productId, list]) => (
        <section key={productId} className="product-group">
          <h2>{productId}</h2>
          <div className="card-grid">
            {list
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((c) => (
                <StatusCard key={c.id} channel={c} nowMs={nowMs} onOpen={onOpen} />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}
